import { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useHabits } from '../stores/habits.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import {
  WEEKS_WINDOW,
  aggregateWeeklyRatios,
  perHabitWeeklyRatios,
} from '../lib/habitStats.js';
import { addDays, mondayOfWeek, todayInParis } from '../lib/dates.js';
import { cn } from '../lib/cn.js';
import { LineChart, type ChartSeries, type XLabel } from './LineChart.js';
import { DEFAULT_HABIT_COLOR, affichee } from '../lib/palette.js';

const OPEN_KEY = 'tracker.chart.open';
const SERIES_KEY = 'tracker.chart.series';
const AGGREGATE_ID = '__aggregate__';
// L'agrégat n'est pas une habitude, donc pas une couleur de donnée : c'est l'état EN COURS de la
// semaine, l'encre courante du thème (Braise 2).
const AGGREGATE_COLOR = 'var(--encre-courant)';

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode / quota — silently ignored
  }
}

function dayOfWeekIndex(today: string): number {
  // Mon=0..Sun=6
  const monday = mondayOfWeek(today);
  let i = 0;
  let cursor = monday;
  while (cursor < today && i < 7) {
    cursor = addDays(cursor, 1);
    i += 1;
  }
  return i;
}

export function TrackerOverviewChart(): React.ReactElement | null {
  const habits = useHabits((s) => s.habits);
  const logs = useHabits((s) => s.logs);
  const isMobile = useIsMobile();
  const [open, setOpen] = useState<boolean>(() => readStorage(OPEN_KEY) !== '0');
  const [series, setSeries] = useState<string>(
    () => readStorage(SERIES_KEY) ?? AGGREGATE_ID
  );

  const today = todayInParis();

  const aggregate = useMemo(
    () => aggregateWeeklyRatios(logs, habits, WEEKS_WINDOW, today),
    [logs, habits, today]
  );
  const perHabit = useMemo(
    () => perHabitWeeklyRatios(logs, habits, WEEKS_WINDOW, today),
    [logs, habits, today]
  );

  if (habits.length === 0) return null;

  function toggleOpen(): void {
    setOpen((o) => {
      const next = !o;
      writeStorage(OPEN_KEY, next ? '1' : '0');
      return next;
    });
  }

  function selectSeries(id: string): void {
    setSeries(id);
    writeStorage(SERIES_KEY, id);
  }

  // Build chronological (oldest → newest) series, currentWeekIdx = last index.
  // Lib returns most-recent first → reverse here once.
  const aggChronological = useMemo(() => [...aggregate].reverse(), [aggregate]);
  const currentWeekIdx = aggChronological.length - 1;

  const aggSeries: ChartSeries = {
    id: AGGREGATE_ID,
    label: 'All habits',
    color: AGGREGATE_COLOR,
    strokeWidth: 2.5,
    points: aggChronological.map((w) => ({ y: w.ratio, raw: w })),
  };

  // A remembered habit that has since been deleted falls back to the aggregate.
  const selected = perHabit.find((s) => s.habit.habitId === series);
  const shownSeries: ChartSeries = selected
    ? {
        id: selected.habit.habitId,
        label: selected.habit.name,
        color: affichee(selected.habit.color ?? DEFAULT_HABIT_COLOR),
        strokeWidth: 2.5,
        points: [...selected.points].reverse().map((w) => ({ y: w.ratio, raw: w })),
      }
    : aggSeries;

  // X labels: every 2 weeks desktop, every 3 mobile, anchored on the newest end.
  const step = isMobile ? 3 : 2;
  const xLabels: XLabel[] = [];
  for (let i = currentWeekIdx; i >= 0; i -= step) {
    const w = aggChronological[i];
    if (w) xLabels.push({ index: i, label: `W${w.weekNum}` });
  }
  xLabels.reverse();

  // Header summary: current week ratio, or "Day d of 7" if early in week with low ratio.
  const currentWeek = aggregate[0]; // most recent
  const dayIdx = dayOfWeekIndex(today); // Mon=0..Sun=6
  const isEarly = dayIdx <= 1;
  let summary: string;
  if (!currentWeek || currentWeek.possible === 0 || currentWeek.ratio === null) {
    summary = '—';
  } else if (isEarly && currentWeek.ratio < 0.25) {
    summary = `Day ${dayIdx + 1} of 7`;
  } else {
    summary = `${Math.round(currentWeek.ratio * 100)}%`;
  }
  // On mobile the header row also hosts the select: the week number is dropped
  // (the week nav below and the x axis both carry it) so the row fits 375px.
  const headerStat = !currentWeek
    ? ''
    : isMobile
      ? summary
      : `Week ${currentWeek.weekNum} · ${summary}`;

  // No data = no actual logs in the 12-week window (a habit created with zero logs
  // would otherwise produce a misleading flat line at 0%).
  const hasAnyData = aggregate.some((w) => w.count > 0);

  const ariaSummary = buildAriaSummary(aggregate, habits.length);

  const chartHeight = isMobile ? 120 : 180;

  return (
    <section role="region" aria-label="Overall habit completion, last 12 weeks" className="graphique p-3 md:p-4">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={toggleOpen}
          aria-expanded={open}
          aria-controls="overview-body"
          className="flex items-center gap-2"
        >
          <span className="carte-titre mb-0">Overview</span>
          <ChevronDown
            size={16}
            aria-hidden
            className={cn('text-muted transition-transform', open && 'rotate-180')}
          />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          {headerStat && <span className="chiffre text-sm text-muted whitespace-nowrap">{headerStat}</span>}
          {open && (
            <select
              value={selected ? series : AGGREGATE_ID}
              onChange={(e) => selectSeries(e.target.value)}
              aria-label="Displayed series"
              className="saisie w-auto max-w-40 text-sm"
            >
              <option value={AGGREGATE_ID}>All habits</option>
              {habits.map((h) => (
                <option key={h.habitId} value={h.habitId}>
                  {h.emoji ? `${h.emoji} ${h.name}` : h.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {open && (
        <div id="overview-body" className="mt-3">
          {!hasAnyData ? (
            <p className="discret text-center py-8">Log at least one day to see the trend.</p>
          ) : (
            <LineChart
              series={[shownSeries]}
              height={chartHeight}
              xLabels={xLabels}
              currentWeekIdx={currentWeekIdx}
              ariaSummary={ariaSummary}
            />
          )}
        </div>
      )}
    </section>
  );
}

function buildAriaSummary(
  aggregate: ReturnType<typeof aggregateWeeklyRatios>,
  habitCount: number
): string {
  const ratios = aggregate.map((w) => w.ratio).filter((r): r is number => r !== null);
  if (ratios.length === 0) {
    return `No data yet across ${habitCount} habit${habitCount === 1 ? '' : 's'}.`;
  }
  const min = Math.round(Math.min(...ratios) * 100);
  const max = Math.round(Math.max(...ratios) * 100);
  const current = aggregate[0]?.ratio;
  const currentStr =
    current === null || current === undefined
      ? ''
      : ` Current week ${Math.round(current * 100)} percent.`;
  return `Aggregate completion across ${habitCount} habit${habitCount === 1 ? '' : 's'}: range ${min} to ${max} percent over the last ${aggregate.length} weeks.${currentStr}`;
}
